import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Spin } from "antd";
import { ModalComponent } from "../pages/candidatePool/ModalComponent.tsx";
import { IPosition, IPositions } from "../services/types.ts";
import { getSearchWhat } from "../services/api.ts";
import styled from "styled-components";
import { colors } from "../util/colors.ts";

interface IProfessionModalProps {
    isModalOpen: boolean;
    onClose: () => void;
    initialValue: string;
    handleSaveProf: (selectedOption: IPosition | null) => void;
}

export const ProfessionModal: FC<IProfessionModalProps> = ({ isModalOpen, onClose, handleSaveProf, initialValue }) => {
    const componentId = useRef(`prof-modal-${Date.now()}`);

    // State
    const [whatSearch, setWhatSearch] = useState('');
    const [allOptions, setAllOptions] = useState<IPositions>([]);
    const [selectedOption, setSelectedOption] = useState<IPosition | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSelectionInProgress, setIsSelectionInProgress] = useState(false);

    // Effect to fetch options when modal opens
    useEffect(() => {
        if (isModalOpen) {
            setIsLoading(true);
            setSelectedOption(null);
            setWhatSearch('');

            getSearchWhat()
                .then((data: IPositions) => {
                    setAllOptions(data);
                })
                .catch(error => {
                    console.error('Error fetching profession options:', error);
                    setAllOptions([]);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [isModalOpen]);

    // Filtered options based on search (memoized)
    const filteredWhatOptions = useMemo(() => {
        if (!whatSearch) {
            return allOptions;
        }
        const lowerSearch = whatSearch.toLowerCase();
        return allOptions.filter(option =>
            option.label.toLowerCase().includes(lowerSearch) ||
            (option.matching_keywords && option.matching_keywords.toLowerCase().includes(lowerSearch))
        );
    }, [allOptions, whatSearch]);

    // Handlers
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setWhatSearch(e.target.value);
    }, []);

    const handleSelectOption = useCallback((option: IPosition) => {
        if (isSelectionInProgress) return;
        setIsSelectionInProgress(true);

        setSelectedOption(option);

        requestAnimationFrame(() => {
            setIsSelectionInProgress(false);
        });
    }, [isSelectionInProgress]);

    const handleSave = useCallback(() => {
        handleSaveProf(selectedOption);
    }, [handleSaveProf, selectedOption]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Memoized list items
    const memoizedOptions = useMemo(() => {
        return filteredWhatOptions.map((option) => (
            <WhatOption
                key={option.id}
                onClick={() => handleSelectOption(option)}
                isSelectedOpt={selectedOption?.id === option.id}
            >
                {option.label}
            </WhatOption>
        ));
    }, [filteredWhatOptions, selectedOption, handleSelectOption]);

    return (
        <ModalComponent
            data-component-id={componentId.current}
            title="Was suchen Sie?"
            open={isModalOpen}
            onClose={handleClose}
            onSave={handleSave}
            footerValue={
                <div style={{ textAlign: 'left', fontStyle: selectedOption ? 'normal' : 'italic' }}>
                    {selectedOption?.label || initialValue || "Kein Filter (Alle Positionen)"}
                </div>
            }
        >
            <Input
                size="large"
                placeholder="Berufsbezeichnung oder Tätigkeit suchen..."
                value={whatSearch}
                onChange={handleSearchChange}
                style={{ marginBottom: 16 }}
                disabled={isLoading || isSelectionInProgress}
            />
            <WhatOptionsWrapper>
                {isLoading && <Spin style={{ display: 'block', margin: '20px auto' }} />}
                {!isLoading && memoizedOptions.length === 0 && whatSearch && (
                    <NoResults>Keine Ergebnisse für "{whatSearch}"</NoResults>
                )}
                {!isLoading && memoizedOptions.length > 0 && (
                    <OptionsContainer>{memoizedOptions}</OptionsContainer>
                )}
                {!isLoading && !whatSearch && memoizedOptions.length === 0 && (
                    <NoResults>Lade Berufsbezeichnungen...</NoResults>
                )}
            </WhatOptionsWrapper>
        </ModalComponent>
    );
};

// Styled Components
const WhatOptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 70vh;
`;

const OptionsContainer = styled.div`
    overflow-y: auto;
    margin-top: 16px;
    @media (max-width: 767px) {
        height: 40vh;
    }
`;

const NoResults = styled.div`
    padding: 20px;
    text-align: center;
    color: ${colors.text};
`;

export const WhatOption = styled.div<{ isSelectedOpt: boolean; disabled?: boolean }>`
    display: flex;
    padding: 12px 16px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-weight: ${props => props.isSelectedOpt ? 'bold' : 'normal'};
    background: ${props => props.isSelectedOpt ? colors.lightBlue : 'white'};

    &:hover {
        transition: all 0.3s ease;
        background-color: ${colors.lightBlue};
        border-radius: 4px;
    }
`;
